import { formatMoneyShort } from "@/components/Plan/MoneyInput";
import { formatMoneyShortBare } from "@/lib/money";
import { AttainmentLine } from "./ActualsCell";
import { PLAN_PRODUCT_COL_WIDTH } from "@/lib/gridLayout";

function formatQty(n: number): string {
  if (!n) return "—";
  return n.toLocaleString("en-US");
}

interface GroupRow {
  asp: { price: number; currency: string } | null;
  months: {
    period_date: string;
    quantity: number;
    actual_quantity: number | null;
    actual_revenue?: number | null;
  }[];
}

interface Group {
  id: string;
  name: string;
  ancestor_names: string[];
  target_revenue: number;
  past_revenue: number;
  rows: GroupRow[];
}

interface Props {
  group: Group;
  months: string[];
  monthTotals: Map<string, number>;
  isPastMonth: (iso: string) => boolean;
  planCurrency?: string | null;
  // Plan-vs-Actual mode: real actual revenue under past months' plan revenue.
  showActuals?: boolean;
  // Vertical hairlines between month columns.
  monthDividers?: boolean;
  // "Oct – Dec 2026" for a revision's plan period: the target number below
  // covers only these months, not the full plan year. Null for a plain version.
  targetPeriodLabel?: string | null;
}

export default function PlanNodeHeaderRow({ group, months, monthTotals, isPastMonth, planCurrency, showActuals = false, monthDividers = showActuals, targetPeriodLabel }: Props) {
  // Per-month revenue for this node and its share of the month's all-nodes total.
  // Plan quantities only (matching the grand totals) — actuals never feed
  // plan-labeled figures. Actual revenue (real ProductSale money) is summed
  // separately and only ever shown as an explicitly labeled second line.
  const perMonth = months.map((m) => {
    const isPast = isPastMonth(m);
    let rev = 0;
    let actualRev = 0;
    for (const r of group.rows) {
      const c = r.months.find((cc) => cc.period_date === m);
      if (!c) continue;
      actualRev += c.actual_revenue ?? 0;
      const price = r.asp?.price ?? 0;
      if (!price) continue;
      rev += (c.quantity || 0) * price;
    }
    const total = monthTotals.get(m) ?? 0;
    const pct = total > 0 ? (rev / total) * 100 : 0;
    return { month: m, isPast, rev, pct, actualRev };
  });

  // Past months are already represented by the grey "past" (actual) segment, so
  // the planned bar only covers current/future months.
  const monthRevenues: { month: string; revenue: number }[] = [];
  let plannedTotal = 0;
  for (const m of months) {
    if (isPastMonth(m)) continue;
    let monthRev = 0;
    for (const r of group.rows) {
      const price = r.asp?.price ?? 0;
      if (!price) continue;
      const c = r.months.find((cc) => cc.period_date === m);
      monthRev += (c?.quantity || 0) * price;
    }
    monthRevenues.push({ month: m, revenue: monthRev });
    plannedTotal += monthRev;
  }

  const target = group.target_revenue;
  const past = group.past_revenue;
  const scale = Math.max(target, past + plannedTotal, 1);
  const remaining = Math.max(0, target - past - plannedTotal);
  const overage = Math.max(0, past + plannedTotal - target);

  // Cumulative segments toward the annual target: realized past (grey),
  // planned within target (purple tint), anything beyond target (over color).
  // Deliberately not month-segmented — per-month attainment lives in the mini
  // bars above; this bar answers "how far toward the year target".
  const plannedWithin = Math.max(0, Math.min(past + plannedTotal, target) - past);

  return (
    <tr style={{ borderColor: "var(--accent)", background: "color-mix(in srgb, var(--primary) 5%, var(--background))" }} className="border-b">
      <td
        style={{ background: "color-mix(in srgb, var(--primary) 5%, var(--background))", borderColor: "var(--border)", width: PLAN_PRODUCT_COL_WIDTH, maxWidth: PLAN_PRODUCT_COL_WIDTH }}
        className="sticky left-0 z-10 px-3 py-1 border-r"
      >
        <div className="flex items-center justify-between gap-2">
          <div
            style={{ color: "var(--muted-foreground)" }}
            className="text-[10px] uppercase tracking-wider truncate"
            title={[ ...group.ancestor_names, group.name ].join(" › ")}
          >
            {group.ancestor_names.map((a) => (
              <span key={a}>{a} › </span>
            ))}
            <span style={{ color: "var(--foreground)" }} className="font-semibold">
              {group.name}
            </span>
          </div>
        </div>
      </td>
      <td
        colSpan={months.length}
        style={{ color: "var(--foreground)", background: "color-mix(in srgb, var(--primary) 5%, var(--background))" }}
        className="p-0 align-middle"
      >
        <div className="py-1">
          {/* Year-target line on top; per-month attainment row below. */}
          <div
            className="flex items-center gap-2 px-2 mb-1 text-[10px] tabular-nums whitespace-nowrap"
            style={{ color: "var(--foreground)" }}
          >
            {past > 0 && (
              <span className="shrink-0" style={{ color: "var(--muted-foreground)" }}>
                Past:{" "}
                <span style={{ color: "var(--foreground)" }} className="font-semibold">
                  {formatMoneyShort(past, planCurrency)}
                </span>
              </span>
            )}
            <div
              className="flex-1 h-1 rounded-full overflow-hidden flex"
              style={{ background: "color-mix(in srgb, var(--foreground) 6%, transparent)" }}
              title={`Past ${formatQty(past)} + Planned ${formatQty(plannedTotal)} vs Target${targetPeriodLabel ? ` (${targetPeriodLabel})` : ""} ${formatQty(target)}`}
            >
              {past > 0 && (
                <div
                  style={{
                    width: `${(past / scale) * 100}%`,
                    background: "color-mix(in srgb, var(--foreground) 18%, transparent)",
                  }}
                />
              )}
              {plannedWithin > 0 && (
                <div
                  style={{
                    width: `${(plannedWithin / scale) * 100}%`,
                    background: "color-mix(in srgb, var(--primary) 22%, transparent)",
                  }}
                />
              )}
              {overage > 0 && (
                <div
                  style={{
                    width: `${(overage / scale) * 100}%`,
                    background: "color-mix(in srgb, var(--chart-3) 45%, transparent)",
                  }}
                />
              )}
            </div>
            <span className="shrink-0">
              {overage > 0 ? (
                <span className="font-semibold" style={{ color: "var(--success)" }}>
                  +{formatMoneyShort(overage, planCurrency)} over
                </span>
              ) : remaining > 0 ? (
                <span style={{ color: "var(--muted-foreground)" }}>
                  {formatMoneyShort(remaining, planCurrency)} left
                </span>
              ) : null}
            </span>
          </div>

          <div className="flex">
            {perMonth.map(({ month, isPast, rev, pct, actualRev }) => (
              <div
                key={month}
                style={{
                  // Fixed share, not flex-1: nowrap content must never let a
                  // month grow wider than its table column above/below.
                  width: `${100 / months.length}%`,
                  ...(monthDividers ? { borderRight: "1px solid var(--accent)" } : {}),
                }}
                className="shrink-0 px-2.5 text-right leading-tight whitespace-nowrap"
              >
                {showActuals && isPast ? (
                  // Past month in actuals mode: actual/plan revenue pair +
                  // attainment %, matching the product cells' format.
                  (rev > 0 || actualRev > 0) && (
                    <span
                      className="block text-left text-[11px] tabular-nums"
                      style={{ color: "var(--muted-foreground)" }}
                      title="Actual revenue (from sales) / plan revenue · attainment"
                    >
                      <span className="font-semibold" style={{ color: "var(--foreground)" }}>
                        {formatMoneyShortBare(actualRev, planCurrency)}
                      </span>
                      {rev > 0 && <>/{formatMoneyShortBare(rev, planCurrency)}</>}
                      {rev > 0 && <AttainmentLine actual={actualRev} plan={rev} />}
                    </span>
                  )
                ) : (
                  <>
                    <span
                      className="text-[11px] tabular-nums font-semibold"
                      style={{ color: "var(--foreground)" }}
                    >
                      {rev > 0 ? formatMoneyShort(rev, planCurrency) : "—"}
                    </span>
                    {rev > 0 && pct > 0 ? (
                      <span
                        className="text-[10px] tabular-nums ml-1"
                        style={{ color: "var(--muted-foreground)" }}
                      >
                        {Math.round(pct)}%
                      </span>
                    ) : null}
                  </>
                )}
              </div>
            ))}
          </div>
        </div>
      </td>
    </tr>
  );
}
